import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, AlertCircleIcon, ShieldCheckIcon, BeakerIcon, AwardIcon, ArrowLeftIcon, StarIcon, DownloadIcon, ShareIcon, BookmarkIcon, BarChart4Icon, ListChecksIcon, FlaskConicalIcon, FileTextIcon } from 'lucide-react';
export const ProductPage = () => {
  const {
    productId
  } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  useEffect(() => {
    // Simulate fetching product data
    const fetchProduct = () => {
      setLoading(true);
      // Mock data - in a real app, this would be fetched from an API
      const mockProducts = [{
        id: '1',
        name: 'BioBalance Pro Prebiotic',
        category: 'Prebiotic',
        company: 'NutriTech Labs',
        image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        healthClaim: 'Supports gut microbiome diversity and digestive health',
        certificationLevel: 3,
        transparencyScore: 92,
        purityResults: 'Passed all safety and purity tests. No contaminants detected.',
        bioavailabilityResults: 'High survivability through digestive simulation. Reaches target areas effectively.',
        efficacySummary: 'Claims fully supported by evidence',
        recommendation: 'Recommended',
        keyFindings: ['Contains all strains listed on label at specified quantities', 'Demonstrates measurable impact on beneficial gut bacteria in lab models', 'Clinical trial showed 42% improvement in microbiome diversity after 8 weeks', 'Statistically significant reduction in digestive discomfort reported by study participants'],
        ingredients: ['Inulin (from chicory root)', 'Fructooligosaccharides (FOS)', 'Galactooligosaccharides (GOS)', 'Resistant Starch', 'Acacia Fiber'],
        testingMethodology: ['Ingredient verification using chromatography', 'Contaminant screening for heavy metals and pesticides', 'In vitro fermentation models to measure prebiotic activity', 'Ex vivo human gut microbiome cultures', 'Randomized controlled trial with 120 participants'],
        clinicalResults: ['Increased abundance of Bifidobacteria by 64% compared to placebo', 'Increased short-chain fatty acid production by 38%', 'Reduced digestive discomfort scores by 47% in IBS-C patients', 'Improved stool consistency in 76% of participants', 'No significant adverse effects reported'],
        certificationDate: '2023-09-15',
        certificationExpiry: '2025-09-15',
        manufacturingLocation: 'San Diego, California, USA',
        dosageInstructions: 'Mix 1 scoop (5g) with water or beverage of choice once daily, preferably with a meal.',
        safetyNotes: 'Generally recognized as safe. May cause mild gas or bloating during first week of use as microbiome adapts.'
      }, {
        id: '2',
        name: 'ImmunoStrength Plus',
        category: 'Immune Support',
        company: 'VitalCore Supplements',
        image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        healthClaim: 'Boosts immune function and reduces inflammation',
        certificationLevel: 2,
        transparencyScore: 78,
        purityResults: 'Passed safety tests. Minor variance in vitamin C content (12% below label claim).',
        bioavailabilityResults: 'Moderate absorption rate in digestive simulation.',
        efficacySummary: 'Claims partially supported by evidence',
        recommendation: 'Conditionally Recommended',
        keyFindings: ['Contains key ingredients but with some variance from label claims', 'Shows moderate anti-inflammatory activity in cell models', 'Increases NK cell activity in lab testing', 'No human clinical trials completed yet to verify immune enhancement claims'],
        ingredients: ['Vitamin C (as ascorbic acid)', 'Vitamin D3 (cholecalciferol)', 'Zinc (as zinc gluconate)', 'Elderberry extract', 'Echinacea purpurea extract', 'Quercetin'],
        testingMethodology: ['HPLC analysis of vitamin content', 'ICP-MS testing for mineral content and heavy metals', 'In vitro inflammatory marker testing using human cell lines', 'NK cell activation assay', 'Antioxidant capacity measurement'],
        clinicalResults: ['Reduced inflammatory cytokine production by 32% in cell models', 'Increased NK cell activity by 47% in ex vivo testing', 'Demonstrated antioxidant activity equivalent to 200mg vitamin C', 'Preliminary pilot study (n=30) showed reduced duration of cold symptoms'],
        certificationDate: '2023-05-22',
        certificationExpiry: '2025-05-22',
        manufacturingLocation: 'Portland, Oregon, USA',
        dosageInstructions: 'Take 2 capsules daily with food.',
        safetyNotes: 'Not recommended for those taking immunosuppressant medications. Consult healthcare provider if pregnant or nursing.'
      }, {
        id: '3',
        name: 'GutGuard Digestive Enzymes',
        category: 'Digestive Support',
        company: 'BioCare Health',
        image: 'https://images.unsplash.com/photo-1550572017-edd951b55104?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        healthClaim: 'Improves nutrient absorption and reduces digestive discomfort',
        certificationLevel: 1,
        transparencyScore: 85,
        purityResults: 'Passed all safety tests. Enzyme activity matches label claims.',
        bioavailabilityResults: 'Not tested at Level 1 certification.',
        efficacySummary: 'Safety and content verified, efficacy not validated',
        recommendation: 'Verified for Safety',
        keyFindings: ['Contains all enzymes listed at specified activity levels', 'Free from harmful contaminants and allergens', 'Manufacturing follows cGMP standards', 'No efficacy testing completed to verify digestive improvement claims'],
        ingredients: ['Amylase', 'Protease', 'Lipase', 'Lactase', 'Cellulase', 'Bromelain', 'Papain'],
        testingMethodology: ['Enzyme activity assays for each component', 'Microbial contamination screening', 'Allergen testing', 'Heavy metal screening', 'Manufacturing facility audit'],
        clinicalResults: ['No clinical efficacy testing performed at Level 1 certification', 'Enzyme activity confirmed in simulated gastric conditions', 'Meets USP standards for enzyme potency'],
        certificationDate: '2023-11-08',
        certificationExpiry: '2024-11-08',
        manufacturingLocation: 'Chicago, Illinois, USA',
        dosageInstructions: 'Take 1 capsule at the beginning of each meal. May increase to 2 capsules for larger meals.',
        safetyNotes: 'Generally well-tolerated. Contains trace amounts of milk proteins from manufacturing process.'
      }, {
        id: '4',
        name: 'MicrobiomePlus Daily',
        category: 'Probiotic',
        company: 'Vitality Sciences',
        image: 'https://images.unsplash.com/photo-1576671414121-aa0c81c869e3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        healthClaim: 'Restores microbiome balance and improves gut health',
        certificationLevel: 2,
        transparencyScore: 88,
        purityResults: 'Passed safety tests. CFU count 15% lower than label claim.',
        bioavailabilityResults: 'Good survivability in acid conditions. Colonization demonstrated in lab models.',
        efficacySummary: 'Claims partially supported by evidence',
        recommendation: 'Recommended with Notes',
        keyFindings: ['Contains all probiotic strains listed but at lower concentrations than claimed', 'Demonstrates ability to temporarily colonize the gut in lab models', 'Shows positive modulation of gut microbiome composition', 'Further human studies needed to validate long-term health benefits'],
        ingredients: ['Lactobacillus acidophilus', 'Bifidobacterium lactis', 'Lactobacillus plantarum', 'Lactobacillus rhamnosus', 'Bifidobacterium longum', 'Inulin (prebiotic)'],
        testingMethodology: ['Strain identification using 16S rRNA sequencing', 'Colony-forming unit (CFU) count verification', 'Acid and bile salt resistance testing', 'Adhesion assays to intestinal cell lines', 'Simulated gut model for colonization assessment'],
        clinicalResults: ['Survived simulated gastric passage with 68% viability', 'Demonstrated adhesion to intestinal cells in vitro', 'Pilot study (n=45) showed increased beneficial bacteria after 4 weeks', 'Reduced bloating reported by 62% of pilot study participants'],
        certificationDate: '2023-08-03',
        certificationExpiry: '2025-08-03',
        manufacturingLocation: 'Boston, Massachusetts, USA',
        dosageInstructions: 'Take 1 capsule daily with a meal. Store in refrigerator for maximum potency.',
        safetyNotes: 'Safe for most individuals. May cause temporary gas or bloating when first starting. Contains trace milk proteins.'
      }];
      const foundProduct = mockProducts.find(p => p.id === productId);
      setTimeout(() => {
        setProduct(foundProduct || null);
        setLoading(false);
      }, 800); // Simulate network delay
    };
    fetchProduct();
  }, [productId]);
  const getCertificationBadge = level => {
    switch (level) {
      case 1:
        return <div className="flex items-center" data-id="element-1103" data-mp="1wsvhf0c4nxmu">
            <ShieldCheckIcon size={20} className="text-blue-600 mr-1" data-id="element-1104" data-mp="1oa0kxfk98ibv" />
            <span className="text-sm font-medium" data-id="element-1105" data-mp="1ocednhrgpig9">
              Level 1: Evidence Screen
            </span>
          </div>;
      case 2:
        return <div className="flex items-center" data-id="element-1106" data-mp="1y87ql293fdyk">
            <BeakerIcon size={20} className="text-blue-600 mr-1" data-id="element-1107" data-mp="1t9wfypaz4zfa" />
            <span className="text-sm font-medium" data-id="element-1108" data-mp="1t7k0qjwlp35x">
              Level 2: Mechanistic Validation
            </span>
          </div>;
      case 3:
        return <div className="flex items-center" data-id="element-1109" data-mp="10mabletx9qrd">
            <AwardIcon size={20} className="text-blue-600 mr-1" data-id="element-1110" data-mp="1f9veb7mvxnyr" />
            <span className="text-sm font-medium" data-id="element-1111" data-mp="1fbgcr8cblr1w">
              Level 3: Clinical Validation
            </span>
          </div>;
      default:
        return null;
    }
  };
  const getRecommendationBadge = recommendation => {
    switch (recommendation) {
      case 'Recommended':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1112" data-mp="hsdildk2rqon">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-1113" data-mp="2mvk8t88gmd6g" />
            Recommended
          </span>;
      case 'Conditionally Recommended':
      case 'Recommended with Notes':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1114" data-mp="2vnvxopnxdskb">
            <AlertCircleIcon size={12} className="mr-1" data-id="element-1115" data-mp="1yywmb1s1oanw" />
            {recommendation}
          </span>;
      case 'Verified for Safety':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1116" data-mp="27yt7uuj50teq">
            <ShieldCheckIcon size={12} className="mr-1" data-id="element-1117" data-mp="2cypbgsnxaitr" />
            Verified for Safety
          </span>;
      case 'Not Recommended':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1118" data-mp="3oct3vuev3ywp">
            <XCircleIcon size={12} className="mr-1" data-id="element-1119" data-mp="2blqdrdoyfz7" />
            Not Recommended
          </span>;
      default:
        return null;
    }
  };
  if (loading) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-1120" data-mp="3oqonh7054nkr">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1121" data-mp="3kpl62g2g2jhf"></div>
        <p className="mt-4 text-gray-600" data-id="element-1122" data-mp="3fqie9xbh69re">Loading product information...</p>
      </div>;
  }
  if (!product) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-1123" data-mp="3aqdsbrrssmts">
        <div className="text-center" data-id="element-1124" data-mp="35skqfaxj2ezb">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1125" data-mp="31qh23lprffwm">
            Product Not Found
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-1126" data-mp="140fs9hpfqlg5">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/product-reviews" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1127" data-mp="100fdfvyfc88k">
            <ArrowLeftIcon size={16} className="mr-2" data-id="element-1128" data-mp="1c0cd3bd4jq3x" />
            Back to Product Reviews
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-white w-full" data-id="element-1129" data-mp="y3yrbq1k52jn">
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-gray-50" data-id="element-1130" data-mp="121mqj5aa9hhc">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1131" data-mp="obawv1y1vao3">
          <div className="mb-6" data-id="element-1132" data-mp="o9zvf8s2uf05">
            <Link to="/product-reviews" className="inline-flex items-center text-blue-600 hover:text-blue-800" data-id="element-1133" data-mp="t31ezu38b2q2">
              <ArrowLeftIcon size={16} className="mr-1" data-id="element-1134" data-mp="ebk352hbczp2" />
              Back to Product Reviews
            </Link>
          </div>
          <div className="bg-white rounded-xl overflow-hidden shadow-md" data-id="element-1135" data-mp="5d725ciwte5t">
            <div className="md:flex" data-id="element-1136" data-mp="5f1kqk6pku6q">
              <div className="md:w-1/3 h-64 md:h-auto" data-id="element-1137" data-mp="acjq60lior9z">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-1138" data-mp="ae4fnbsz5k0z" />
              </div>
              <div className="p-8 md:w-2/3" data-id="element-1139" data-mp="3hyds0epe2og6">
                <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-1140" data-mp="3vwu0aa2wgn0f">
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide" data-id="element-1141" data-mp="3vtzhso3g5c9n">
                    {product.category}
                  </span>
                  {getRecommendationBadge(product.recommendation)}
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-1142" data-mp="4o6mcf6zo22w">
                  {product.name}
                </h1>
                <p className="text-gray-600 mb-4" data-id="element-1143" data-mp="efascpeoscll">
                  By <span className="font-medium" data-id="element-1144" data-mp="egm3jaagommq">{product.company}</span>
                </p>
                <div className="mb-6" data-id="element-1145" data-mp="sfvb5lklq8f3">
                  {getCertificationBadge(product.certificationLevel)}
                </div>
                <div className="mb-6" data-id="element-1146" data-mp="3h8h0rneq8fzl">
                  <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1147" data-mp="3h9jnrk8xhya8">
                    Health Claim
                  </h2>
                  <p className="text-gray-700 italic" data-id="element-1148" data-mp="3q96saewyfk7i">
                    "{product.healthClaim}"
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 mb-6" data-id="element-1149" data-mp="3v0x64uv3oq3o">
                  <div data-id="element-1150" data-mp="3tknebekk4ln">
                    <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1151" data-mp="3udmcq7adpt8">
                      Transparency Score
                    </h2>
                    <div className="flex items-center" data-id="element-1152" data-mp="8ui2gf4a8iix">
                      <div className="w-32 bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-1153" data-mp="dt35emax5gb2">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{
                        width: `${product.transparencyScore}%`
                      }} data-id="element-1154" data-mp="hsbzjs9vfg75"></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700" data-id="element-1155" data-mp="2c1qt07a3k2xc">
                        {product.transparencyScore}%
                      </span>
                    </div>
                  </div>
                  <div data-id="element-1156" data-mp="22amjvo73rcaf">
                    <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1157" data-mp="22bxkxg95y5xu">
                      Certification Date
                    </h2>
                    <p className="text-gray-700" data-id="element-1158" data-mp="2v0l8jihdx7ek">{product.certificationDate}</p>
                  </div>
                  <div data-id="element-1159" data-mp="2q0qjzll30two">
                    <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1160" data-mp="2q29yp7r3awwc">
                      Valid Until
                    </h2>
                    <p className="text-gray-700" data-id="element-1161" data-mp="2h0tqn6nqz6va">
                      {product.certificationExpiry}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3" data-id="element-1162" data-mp="1ahvikzakaxeb">
                  <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1163" data-mp="15k3ukgd1fvfe">
                    <DownloadIcon size={16} className="mr-2" data-id="element-1164" data-mp="35pr474h95t23" />
                    Download Report
                  </button>
                  <button className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors" data-id="element-1165" data-mp="2vpqijbvwv0uq">
                    <ShareIcon size={16} className="mr-2" data-id="element-1166" data-mp="30otlmqsbyx02" />
                    Share
                  </button>
                  <button className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors" data-id="element-1167" data-mp="3ofz3fbhtzzd0">
                    <BookmarkIcon size={16} className="mr-2" data-id="element-1168" data-mp="3eoup8dzzcij8" />
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Tabs Section */}
      <section className="py-8 bg-white border-b" data-id="element-1169" data-mp="b61e07uoc5s8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1170" data-mp="fykupdonrh19">
          <div className="flex overflow-x-auto scrollbar-hide space-x-8" data-id="element-1171" data-mp="fzvwx92gggq9">
            <button className={`pb-4 px-1 font-medium text-sm flex items-center whitespace-nowrap ${activeTab === 'overview' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('overview')} data-id="element-1172" data-mp="20mwvfubv25fq">
              <FileTextIcon size={16} className="mr-2" data-id="element-1173" data-mp="25fq2iousxpuz" />
              Overview
            </button>
            <button className={`pb-4 px-1 font-medium text-sm flex items-center whitespace-nowrap ${activeTab === 'testing' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('testing')} data-id="element-1174" data-mp="1hq5h7zb6egij">
              <BeakerIcon size={16} className="mr-2" data-id="element-1175" data-mp="1lpnrm15df974" />
              Testing Methodology
            </button>
            <button className={`pb-4 px-1 font-medium text-sm flex items-center whitespace-nowrap ${activeTab === 'results' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('results')} data-id="element-1176" data-mp="323max6g35ev4">
              <BarChart4Icon size={16} className="mr-2" data-id="element-1177" data-mp="2hzgjx8nypxpu" />
              Results & Findings
            </button>
            <button className={`pb-4 px-1 font-medium text-sm flex items-center whitespace-nowrap ${activeTab === 'usage' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('usage')} data-id="element-1178" data-mp="2wwhctfhw3ypw">
              <ListChecksIcon size={16} className="mr-2" data-id="element-1179" data-mp="30w87vveg9108" />
              Usage & Safety
            </button>
          </div>
        </div>
      </section>
      {/* Content Section */}
      <section className="py-12 bg-gray-50" data-id="element-1180" data-mp="3anm7022hnysu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1181" data-mp="3aox8fv8gougs">
          {activeTab === 'overview' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white p-8 rounded-lg shadow-sm" data-id="element-1182" data-mp="3kkquq6bzg2br">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1183" data-mp="3u7uonewk78wg">
                Product Overview
              </h2>
              <div className="mb-8" data-id="element-1184" data-mp="34607wtardu7">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1185" data-mp="3ki1fekjw35vd">
                  Key Findings
                </h3>
                <ul className="space-y-3" data-id="element-1186" data-mp="3pa1kssiqaqzc">
                  {product.keyFindings.map((finding, index) => <li key={index} className="flex items-start" data-id="element-1187" data-mp="3bjg1x3t9yh1g" data-mpi={index}>
                      <CheckCircleIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1188" data-mp="3fiovo8obeex0" />
                      <span className="text-gray-700" data-id="element-1189" data-mp="36is9qetb7u5n">{finding}</span>
                    </li>)}
                </ul>
              </div>
              <div className="mb-8" data-id="element-1190" data-mp="1c44p32612lyn">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1191" data-mp="1c4edwmfc6n2r">
                  Ingredients
                </h3>
                <div className="bg-gray-50 p-6 rounded-md" data-id="element-1192" data-mp="139qdb9kphtka">
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1193" data-mp="1385j5l2b0cmy">
                    {product.ingredients.map((ingredient, index) => <li key={index} className="flex items-center" data-id="element-1194" data-mp="1v1pgoh06fymy" data-mpi={index}>
                        <div className="h-2 w-2 bg-blue-600 rounded-full mr-3" data-id="element-1195" data-mp="1qzue796iff7l"></div>
                        <span className="text-gray-700" data-id="element-1196" data-mp="1qxzshsnezj2g">{ingredient}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1197" data-mp="1h1ep1br1mg0q">
                <div className="bg-blue-50 p-6 rounded-lg" data-id="element-1198" data-mp="amm56vqbgz4a">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1199" data-mp="5mrb6jb8vtf0">
                    Purity Results
                  </h3>
                  <p className="text-gray-700" data-id="element-1200" data-mp="3rp00it19a2va">{product.purityResults}</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-lg" data-id="element-1201" data-mp="ip7nk6eg8f1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1202" data-mp="5gqqq53ts7qi">
                    Bioavailability Results
                  </h3>
                  <p className="text-gray-700" data-id="element-1203" data-mp="a8iey8zie1dh">
                    {product.bioavailabilityResults}
                  </p>
                </div>
                <div className="bg-blue-50 p-6 rounded-lg" data-id="element-1204" data-mp="ebeshla6z7ak">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1205" data-mp="j6u406kq8r3t">
                    Efficacy Summary
                  </h3>
                  <p className="text-gray-700" data-id="element-1206" data-mp="o5dtggyj4l0x">{product.efficacySummary}</p>
                </div>
              </div>
            </motion.div>}
          {activeTab === 'testing' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white p-8 rounded-lg shadow-sm" data-id="element-1207" data-mp="2q0o8j253ka4q">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1208" data-mp="y90zwtoybick">
                Testing Methodology
              </h2>
              <div className="mb-8" data-id="element-1209" data-mp="tbsemk8iay73">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1210" data-mp="5l55xwqro4jh">
                  Certification Level Explained
                </h3>
                <div className="bg-gray-50 p-6 rounded-md mb-6" data-id="element-1211" data-mp="1l4dv9aqvuty">
                  {product.certificationLevel === 1 && <div className="flex items-start" data-id="element-1212" data-mp="fdavkztjq9vi">
                      <ShieldCheckIcon size={24} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1213" data-mp="ak9gortcaabx" />
                      <div data-id="element-1214" data-mp="3e1ht2w5ashz9">
                        <h4 className="font-medium text-gray-900 mb-2" data-id="element-1215" data-mp="3e06poxfrlc8o">
                          Level 1: Evidence Screen
                        </h4>
                        <p className="text-gray-700" data-id="element-1216" data-mp="1thcolo4mcxsy">
                          This product has undergone basic safety testing and
                          ingredient verification. We've confirmed what's in the
                          product matches the label and that it's free from
                          harmful contaminants. However, at Level 1, we have not
                          verified the product's efficacy claims through
                          mechanistic or clinical testing.
                        </p>
                      </div>
                    </div>}
                  {product.certificationLevel === 2 && <div className="flex items-start" data-id="element-1217" data-mp="286sb2rx4ah2u">
                      <BeakerIcon size={24} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1218" data-mp="ru3ifqqxehbg" />
                      <div data-id="element-1219" data-mp="wu7y5ek0f80m">
                        <h4 className="font-medium text-gray-900 mb-2" data-id="element-1220" data-mp="cqskjxnazfol">
                          Level 2: Mechanistic Validation
                        </h4>
                        <p className="text-gray-700" data-id="element-1221" data-mp="8pqiza5wyrjg">
                          This product has passed safety testing and ingredient
                          verification. Additionally, it has demonstrated
                          biological activity in laboratory models that supports
                          its mechanism of action. This means there is
                          scientific evidence showing how the product works in
                          controlled settings, but full clinical validation in
                          humans may still be pending.
                        </p>
                      </div>
                    </div>}
                  {product.certificationLevel === 3 && <div className="flex items-start" data-id="element-1222" data-mp="1aekmf4xb352i">
                      <AwardIcon size={24} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1223" data-mp="1afvon0b3s4ri" />
                      <div data-id="element-1224" data-mp="1mwmxltbql6bw">
                        <h4 className="font-medium text-gray-900 mb-2" data-id="element-1225" data-mp="1mvve3gveyz1z">
                          Level 3: Clinical Validation
                        </h4>
                        <p className="text-gray-700" data-id="element-1226" data-mp="1wmq1iokec4p0">
                          This product has achieved our highest level of
                          certification. It has passed safety testing,
                          demonstrated its mechanism of action in laboratory
                          models, and shown efficacy in human clinical trials.
                          The health claims made by the manufacturer are
                          supported by robust scientific evidence including
                          controlled human studies.
                        </p>
                      </div>
                    </div>}
                </div>
              </div>
              <div className="mb-8" data-id="element-1227" data-mp="3afpdi9uqlkbw">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1228" data-mp="3agqz389dpp7v">
                  Testing Procedures
                </h3>
                <ul className="space-y-4" data-id="element-1229" data-mp="35ifpmymzxgq6">
                  {product.testingMethodology.map((method, index) => <li key={index} className="flex items-start" data-id="element-1230" data-mp="3k7by2wsysvrb" data-mpi={index}>
                      <FlaskConicalIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1231" data-mp="3fd7p68052dnl" />
                      <span className="text-gray-700" data-id="element-1232" data-mp="3o7vrpqebxh8p">{method}</span>
                    </li>)}
                </ul>
              </div>
              <div data-id="element-1233" data-mp="6wwaus3hyfwi">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1234" data-mp="205mlhhjge00">
                  Manufacturing Information
                </h3>
                <div className="bg-gray-50 p-6 rounded-md" data-id="element-1235" data-mp="28r53ydrify3t">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1236" data-mp="23rip5zw1a9sw">
                    <div data-id="element-1237" data-mp="23tbw7gak6w1f">
                      <h4 className="font-medium text-gray-900 mb-2" data-id="element-1238" data-mp="j9gnkchlwd8q">
                        Manufacturing Location
                      </h4>
                      <p className="text-gray-700" data-id="element-1239" data-mp="o7rw8jw60hpd">
                        {product.manufacturingLocation}
                      </p>
                    </div>
                    <div data-id="element-1240" data-mp="ehyjhfkadwjj">
                      <h4 className="font-medium text-gray-900 mb-2" data-id="element-1241" data-mp="eh6zz33yrp9m">
                        Certification Valid Until
                      </h4>
                      <p className="text-gray-700" data-id="element-1242" data-mp="l36jc0wzky7">
                        {product.certificationExpiry}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>}
          {activeTab === 'results' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white p-8 rounded-lg shadow-sm" data-id="element-1243" data-mp="3rquv46z1mpiw">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1244" data-mp="2wp5y99nmvmdz">
                Results & Findings
              </h2>
              <div className="mb-8" data-id="element-1245" data-mp="31le765b8mjcp">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1246" data-mp="36epgqe7fpe8b">
                  Clinical Results
                </h3>
                {product.clinicalResults && product.clinicalResults.length > 0 ? <ul className="space-y-3" data-id="element-1247" data-mp="3fdkm5o2nmkak">
                    {product.clinicalResults.map((result, index) => <li key={index} className="flex items-start" data-id="element-1248" data-mp="3keruppcmfnia" data-mpi={index}>
                        <BarChart4Icon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1249" data-mp="3pc0etvhby1m6" />
                        <span className="text-gray-700" data-id="element-1250" data-mp="1uysj81lj5lzh">{result}</span>
                      </li>)}
                  </ul> : <p className="text-gray-700 italic" data-id="element-1251" data-mp="1nhm4x6t1a6m0">
                    No clinical results available at this certification level.
                  </p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1252" data-mp="1dpzlin6jl186">
                <div data-id="element-1253" data-mp="18ry2u3d133x8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1254" data-mp="18sg81pho4sha">
                    Efficacy Assessment
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-md" data-id="element-1255" data-mp="21fk8dh5tgimq">
                    <div className="flex items-center mb-4" data-id="element-1256" data-mp="21d7rl7bsoeb9">
                      {product.recommendation === 'Recommended' && <CheckCircleIcon size={24} className="text-green-600 mr-3" data-id="element-1257" data-mp="1rhd2btd2fkzi" />}
                      {(product.recommendation === 'Conditionally Recommended' || product.recommendation === 'Recommended with Notes') && <AlertCircleIcon size={24} className="text-yellow-600 mr-3" data-id="element-1258" data-mp="38987fbwfzwra" />}
                      {product.recommendation === 'Verified for Safety' && <ShieldCheckIcon size={24} className="text-blue-600 mr-3" data-id="element-1259" data-mp="2zawkpkyp5j44" />}
                      {product.recommendation === 'Not Recommended' && <XCircleIcon size={24} className="text-red-600 mr-3" data-id="element-1260" data-mp="2bkacntoozyu4" />}
                      <h4 className="font-medium text-gray-900" data-id="element-1261" data-mp="2gjdjnjo8gf4q">
                        {product.recommendation}
                      </h4>
                    </div>
                    <p className="text-gray-700" data-id="element-1262" data-mp="26jv197nz11es">{product.efficacySummary}</p>
                  </div>
                </div>
                <div data-id="element-1263" data-mp="1mxkt23oqz1ih">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1264" data-mp="1mz4a3wi89glb">
                    Transparency Assessment
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-md" data-id="element-1265" data-mp="1wwiozroi5xbi">
                    <div className="mb-4" data-id="element-1266" data-mp="1wy3iddz2za7s">
                      <div className="flex justify-between mb-1" data-id="element-1267" data-mp="1rxpabvxhc09r">
                        <span className="text-sm font-medium text-gray-700" data-id="element-1268" data-mp="1rw4iie2juvfl">
                          Transparency Score
                        </span>
                        <span className="text-sm font-medium text-gray-700" data-id="element-1269" data-mp="21weuxwgrfytp">
                          {product.transparencyScore}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1270" data-mp="2anf4qhxbyk15">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{
                      width: `${product.transparencyScore}%`
                    }} data-id="element-1271" data-mp="2pl7hx3femwc6"></div>
                      </div>
                    </div>
                    <p className="text-gray-700" data-id="element-1272" data-mp="my98867sb80n">
                      {product.transparencyScore >= 90 ? 'Excellent transparency. The manufacturer provided complete access to data, formulations, and manufacturing processes.' : product.transparencyScore >= 75 ? 'Good transparency. The manufacturer shared most key information but may have withheld some proprietary details.' : product.transparencyScore >= 60 ? 'Moderate transparency. The manufacturer provided basic information but limited access to complete data or methodology.' : 'Limited transparency. The manufacturer was reluctant to share important information about the product or its testing.'}
                    </p>
                  </div>
                </div>
              </div>
              <div data-id="element-1273" data-mp="39usal1a84t7w">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1274" data-mp="39u0gxw06t5k9">
                  Health Claim Assessment
                </h3>
                <div className="bg-blue-50 p-6 rounded-md" data-id="element-1275" data-mp="34wqsaikmgxxh">
                  <p className="text-gray-700 italic mb-4" data-id="element-1276" data-mp="30475qw3yisgj">
                    "{product.healthClaim}"
                  </p>
                  <p className="text-gray-700" data-id="element-1277" data-mp="2v5mevnalhl4r">
                    {product.recommendation === 'Recommended' ? 'Our testing fully supports this health claim based on comprehensive evidence including clinical trials.' : product.recommendation === 'Conditionally Recommended' || product.recommendation === 'Recommended with Notes' ? 'Our testing partially supports this health claim. There is some evidence for the claimed benefits, but additional research would strengthen the claim.' : product.recommendation === 'Verified for Safety' ? "While we've verified this product is safe and contains the listed ingredients, we have not validated the efficacy of this health claim." : 'Our testing does not support this health claim based on available evidence.'}
                  </p>
                </div>
              </div>
            </motion.div>}
          {activeTab === 'usage' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white p-8 rounded-lg shadow-sm" data-id="element-1278" data-mp="2nn658b2mrboc">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1279" data-mp="128sawkv3walj">
                Usage & Safety Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1280" data-mp="179697b6wo66d">
                <div data-id="element-1281" data-mp="3lfcu142j5ic4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1282" data-mp="3leun9di8rlpy">
                    Recommended Usage
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-md" data-id="element-1283" data-mp="3gjfciufj65xr">
                    <p className="text-gray-700" data-id="element-1284" data-mp="3ufj5ylikya96">
                      {product.dosageInstructions}
                    </p>
                  </div>
                </div>
                <div data-id="element-1285" data-mp="32p4wd1hlnk3b">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1286" data-mp="32qfyyxzb6lsu">
                    Safety Notes
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-md" data-id="element-1287" data-mp="2xogv7mznpmvd">
                    <p className="text-gray-700" data-id="element-1288" data-mp="3bizz62v90yfs">{product.safetyNotes}</p>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-1289" data-mp="36p5asm4p7vad">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1290" data-mp="2j15i3gnttut5">
                  Ingredients
                </h3>
                <div className="bg-gray-50 p-6 rounded-md" data-id="element-1291" data-mp="2ezajwl16zxjl">
                  <ul className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-1292" data-mp="33v4h252l7sr7">
                    {product.ingredients.map((ingredient, index) => <li key={index} className="flex items-center" data-id="element-1293" data-mp="38uqykqpg7v5t" data-mpi={index}>
                        <div className="h-2 w-2 bg-blue-600 rounded-full mr-3" data-id="element-1294" data-mp="3cv9q0jnfiz7h"></div>
                        <span className="text-gray-700" data-id="element-1295" data-mp="3hue0ddm3b2ze">{ingredient}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              <div data-id="element-1296" data-mp="3rstr3fh7ga8d">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1297" data-mp="3rsa5noscj3pl">
                  Important Disclaimers
                </h3>
                <div className="bg-blue-50 p-6 rounded-md" data-id="element-1298" data-mp="enz388hpcsk">
                  <ul className="space-y-3" data-id="element-1299" data-mp="4g982ihwl6xh">
                    <li className="flex items-start" data-id="element-1300" data-mp="4f7rxzm15u8w">
                      <AlertCircleIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1301" data-mp="9fccq1tz1b4x" />
                      <p className="text-gray-700" data-id="element-1302" data-mp="ebklmxhks83n">
                        Ukweli certification does not replace medical advice.
                        Consult with a healthcare professional before starting
                        any supplement.
                      </p>
                    </li>
                    <li className="flex items-start" data-id="element-1303" data-mp="elhgsexli7nv">
                      <AlertCircleIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1304" data-mp="aje45mz6wx10" />
                      <p className="text-gray-700" data-id="element-1305" data-mp="128qr059slc9b">
                        Individual results may vary. Our testing evaluates
                        general efficacy, but responses can differ based on
                        individual factors.
                      </p>
                    </li>
                    <li className="flex items-start" data-id="element-1306" data-mp="1pzcxvt828qhq">
                      <AlertCircleIcon size={20} className="text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1307" data-mp="1q04lagri7hwx" />
                      <p className="text-gray-700" data-id="element-1308" data-mp="3gktdxfuq90c3">
                        This product has not been evaluated by the FDA. It is
                        not intended to diagnose, treat, cure, or prevent any
                        disease.
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>}
        </div>
      </section>
      {/* Related Products Section */}
      <section className="py-16 bg-white" data-id="element-1309" data-mp="2swjxyuk736sv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1310" data-mp="2svsdaes4ythd">
          <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1311" data-mp="2nx7emjsj8hv1">
            Similar Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1312" data-mp="2jwdqpqf3b325">
            {[1, 2, 3].map(id => {
            if (id.toString() === productId) return null;
            return <Link key={id} to={`/product/${id}`} className="bg-gray-50 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-1313" data-mp="i3z80p4q1uko">
                  <div className="h-48 overflow-hidden" data-id="element-1314" data-mp="2506t460cfvww">
                    <img src={`https://images.unsplash.com/photo-${id === 1 ? '1584308666744-24d5c474f2ae' : id === 2 ? '1587854692152-cbe660dbde88' : '1550572017-edd951b55104'}?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80`} alt="Product" className="w-full h-full object-cover" data-id="element-1315" data-mp="29rx8iqe519v6" />
                  </div>
                  <div className="p-4" data-id="element-1316" data-mp="1m47a3kpcg2pg">
                    <div className="flex justify-between items-start mb-2" data-id="element-1317" data-mp="1q3ofyf8on1v5">
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide" data-id="element-1318" data-mp="1v2sqb97cf5n2">
                        {id === 1 ? 'Prebiotic' : id === 2 ? 'Immune Support' : 'Digestive Support'}
                      </span>
                      {id === 1 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1319" data-mp="12f4prx336i6v">
                          <CheckCircleIcon size={10} className="mr-1" data-id="element-1320" data-mp="1gc1n8hdpmxwq" />
                          Recommended
                        </span> : id === 2 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1321" data-mp="nq03a3p9drsy">
                          <AlertCircleIcon size={10} className="mr-1" data-id="element-1322" data-mp="xf9wjmvu9gin" />
                          Conditional
                        </span> : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1323" data-mp="sn8oye9m8dyy">
                          <ShieldCheckIcon size={10} className="mr-1" data-id="element-1324" data-mp="5pnjx47z7n" />
                          Verified
                        </span>}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-1325" data-mp="2gdy0kd92zuzn">
                      {id === 1 ? 'BioBalance Pro Prebiotic' : id === 2 ? 'ImmunoStrength Plus' : 'GutGuard Digestive Enzymes'}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3" data-id="element-1326" data-mp="2udhanblgdgel">
                      By{' '}
                      {id === 1 ? 'NutriTech Labs' : id === 2 ? 'VitalCore Supplements' : 'BioCare Health'}
                    </p>
                    <div className="flex items-center" data-id="element-1327" data-mp="2qakl10xhwk15">
                      {id === 1 ? <AwardIcon size={16} className="text-blue-600 mr-1" data-id="element-1328" data-mp="19x2vdqi5d94t" /> : id === 2 ? <BeakerIcon size={16} className="text-blue-600 mr-1" data-id="element-1329" data-mp="1eya2rogdo6ay" /> : <ShieldCheckIcon size={16} className="text-blue-600 mr-1" data-id="element-1330" data-mp="1u8otgio9ogln" />}
                      <span className="text-xs font-medium text-gray-700" data-id="element-1331" data-mp="1khbznliwrgcz">
                        Level {id === 1 ? '3' : id === 2 ? '2' : '1'}{' '}
                        Certification
                      </span>
                    </div>
                  </div>
                </Link>;
          })}
          </div>
        </div>
      </section>
    </div>;
};